import { decodeCsvBytes, toCsvText } from "./csvParse";

/** File types the table box takes. Excel files are read in the browser; nothing is uploaded. */
export const MERGE_ACCEPT = ".csv,.tsv,.txt,.xlsx,.xlsm,.xls,.ods";
const SHEET_EXT = /\.(xlsx|xlsm|xls|ods)$/i;
export const isSheetFile = (name: string) => SHEET_EXT.test(name);

/** Rows from a spreadsheet as tab-separated text, the form pasted Excel cells arrive in, so both take the same road. */
export function rowsToTabText(rows: readonly (readonly unknown[])[]): string {
  const cells = rows.map((r) => r.map((c) => String(c ?? "").trim()));
  const width = cells.reduce((m, r) => Math.max(m, r.length), 0);
  // keep every row as wide as the widest, so a short row does not shift into the wrong column
  return toCsvText(cells.filter((r) => r.some(Boolean)).map((r) => r.concat(Array(width - r.length).fill(""))), "\t");
}

/** The text for the table box from a CSV, TSV, text or Excel file. For a workbook it is the first visible sheet. */
export async function mergeFileText(file: File): Promise<{ text: string; note: string }> {
  const buf = await file.arrayBuffer();
  if (!isSheetFile(file.name)) return { text: decodeCsvBytes(buf).text, note: "" };
  const XLSX = await import("xlsx");
  const book = XLSX.read(buf, { type: "array" });
  const visible = book.SheetNames.filter((_, i) => !book.Workbook?.Sheets?.[i]?.Hidden);
  const name = (visible.length ? visible : book.SheetNames)[0];
  if (!name) throw new Error("That workbook has no sheets.");
  const rows = XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[name], { header: 1, raw: false, defval: "" });
  return { text: rowsToTabText(rows), note: book.SheetNames.length > 1 ? `Read the sheet "${name}". Only the first sheet is used.` : "" };
}

/**
 * A first template built from the table's own column names, so the preview shows the first record as soon as the
 * data is in, before the person has picked a preset or written anything. The first column is the greeting or the
 * name; the next few follow as "Column: value" lines.
 */
export function starterTemplate(kind: "letter" | "certificate", headers: readonly string[]): string {
  const cols = headers.map((h) => h.trim()).filter(Boolean);
  if (cols.length === 0) return "";
  const [first, ...rest] = cols;
  const lines = rest.slice(0, 4).map((h) => `${h}: {{${h}}}`);
  if (kind === "certificate") return ["Certificate", "", "This is to certify that", "", `{{${first}}}`, ...(lines.length ? ["", ...lines] : [])].join("\n");
  return [`Dear {{${first}}},`, ...(lines.length ? ["", ...lines] : [])].join("\n");
}
